import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Network } from "lucide-react";

/**
 * v3.99e — Shared parentage / pedigree tree.
 *
 * Extracted from LineageDetail.jsx so the same visual tree can be embedded
 * inside the listing/product detail page (where it surfaces "this strain's
 * known parents + descendants" right next to the buy CTA). Renders:
 *
 *   grandparents row (optional)
 *           ↓
 *       parents row  (foundation banner if no parents recorded)
 *           ↓
 *       THIS STRAIN  (gold tile)
 *           ↓
 *      direct descendants  (top 4)
 *
 * Behavior:
 *   - Parent/grandparent/descendant tiles are <Link>s to /strains/<slug>
 *     when the lineage record exists; otherwise rendered as plain tiles.
 *   - Honors "foundation / landrace" semantics — strains with placeholder
 *     parent text ("NA", "Landrace", "Unknown", etc) get the foundation
 *     banner instead of "Unknown × Unknown".
 *   - The optional "Map this strain" button (default ON) links straight
 *     to the genetics map anchored on this strain.
 */
const INVALID_PARENT_RX = /^(na|n\/a|n\.a\.|none|null|nil|-+|\?+|tbd|tba|unknown(\s+parent(age|s)?)?|no\s+(known\s+)?parent(s|age)?|not\s+(known|available|applicable|recorded|specified)|landrace(\s+population)?|regional\s+(landrace|population).*|open[-\s]pollinated\s+population|foundation(\s+line)?|root(\s+lineage)?)$/i;
const INVALID_PARENT_SUBSTRINGS = [
  "regional landrace", "landrace population",
  "regional population", "open pollinated population",
  "open-pollinated population", "no known parentage",
  "unknown parentage", "not applicable", "not recorded",
];

export function isInvalidParentText(text) {
  if (!text) return true;
  const s = String(text).trim().toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/^[\s.,;:/\\"'()[\]{}]+|[\s.,;:/\\"'()[\]{}]+$/g, "");
  if (!s) return true;
  if (INVALID_PARENT_RX.test(s)) return true;
  return INVALID_PARENT_SUBSTRINGS.some((sub) => s.includes(sub));
}

export default function ParentageTree({ doc, showMapButton = true, compact = false }) {
  const parents = doc.parent_links || {};
  const grandparents = doc.grandparents || {};
  const children = (doc.children || []).slice(0, compact ? 3 : 4);
  const hasChildren = children.length > 0;
  const hasGrand = !compact && Object.values(grandparents).some((g) => g && (g.parent_one || g.parent_two));
  const p1Valid = !isInvalidParentText(doc.parent_one);
  const p2Valid = !isInvalidParentText(doc.parent_two);
  const isFoundation = !p1Valid && !p2Valid;
  const isLandrace = !!(doc.landrace || doc.heirloom || doc.preservation);
  return (
    <section id="parentage" className={`scroll-mt-8 bg-[var(--surface)] border border-[var(--border)] ${compact ? "p-4 md:p-5" : "p-5 md:p-8"} mb-6 overflow-x-auto`}
             data-testid="lineage-parentage-tree">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <span style={{ color: "var(--gold)" }}>●</span> Cannabis Genetics Map
          <span className="hidden md:inline text-[10px] opacity-60">· parents · descendants · related crosses</span>
        </div>
        {showMapButton && (
          <Link to={`/lineage?tab=map&center=${encodeURIComponent(doc.slug || doc.id)}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] font-mono uppercase tracking-[0.16em] transition-colors hover:bg-[var(--bg-2)]"
                style={{ borderColor: "var(--gold)", color: "var(--gold)", borderRadius: 2 }}
                data-testid="lineage-map-this-strain">
            <Network className="w-3 h-3" /> Map this strain
          </Link>
        )}
      </div>
      <div className={`relative ${compact ? "" : "md:min-w-[720px]"}`}>
        {hasGrand && (
          <>
            <div className="flex items-stretch justify-center gap-1.5 md:gap-4" data-testid="parentage-grandparents-row">
              {["parent_one", "parent_two"].map((branch) => {
                const node = grandparents[branch] || {};
                return (
                  <div key={branch} className="flex items-stretch gap-2 md:gap-3">
                    {["parent_one", "parent_two"].map((gKey, gi) => {
                      const gp = node[gKey];
                      if (!gp) return <div key={gKey} className="w-[100px] md:w-[140px]"/>;
                      return (
                        <TreeNode
                          key={`${branch}-${gKey}`}
                          label={gp.strain_name}
                          slug={gp.slug || gp.id}
                          status={gp.verification_status}
                          hasListings={gp.has_listings}
                          role="grandparent"
                          compact
                          testid={`parentage-node-grandparent-${branch}-${gi}`}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center my-2 gap-[110px] sm:gap-[180px] md:gap-[340px]">
              <div className="w-px h-6" style={{ background: "var(--gold)", opacity: 0.6 }}></div>
              <div className="w-px h-6" style={{ background: "var(--gold)", opacity: 0.6 }}></div>
            </div>
          </>
        )}
        {isFoundation ? (
          <div className="flex justify-center" data-testid="parentage-foundation-banner">
            <div className="px-5 py-4 border max-w-[520px] text-center"
                 style={{ borderColor: "var(--gold)", background: "rgba(212,175,55,0.05)", borderRadius: 2 }}>
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] mb-1"
                   style={{ color: "var(--gold)" }}>
                {isLandrace ? "Foundation / landrace line" : "Root lineage"}
              </div>
              <div className="text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>
                {isLandrace
                  ? "No parent strains recorded — this is a foundation cultivar at the top of the tree."
                  : "Parentage not applicable or not recorded for this strain."}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-stretch justify-center gap-3 md:gap-6">
            <TreeNode
              label={p1Valid ? doc.parent_one : "Unknown"}
              slug={parents.parent_one?.slug || parents.parent_one?.id}
              status={parents.parent_one?.verification_status}
              hasListings={parents.parent_one?.has_listings}
              role="parent"
              compact={compact}
              testid="parentage-node-parent-1"
            />
            <div className={`flex items-center font-serif ${compact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"} shrink-0`}
                 style={{ color: "var(--gold)" }}>×</div>
            <TreeNode
              label={p2Valid ? doc.parent_two : "Unknown"}
              slug={parents.parent_two?.slug || parents.parent_two?.id}
              status={parents.parent_two?.verification_status}
              hasListings={parents.parent_two?.has_listings}
              role="parent"
              compact={compact}
              testid="parentage-node-parent-2"
            />
          </div>
        )}
        {!isFoundation && (
          <div className="flex justify-center my-3">
            <div className="w-px h-8" style={{ background: "var(--gold)" }}></div>
          </div>
        )}
        <div className="flex justify-center">
          <div className={`bg-[var(--gold)] text-[#0B0B0B] text-center ${compact ? "px-5 py-3 min-w-[180px]" : "px-6 py-4 md:px-10 md:py-5 min-w-[200px]"}`}
               style={{ borderRadius: 2 }}
               data-testid="parentage-node-current">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">This strain</div>
            <div className={`font-serif ${compact ? "text-lg md:text-xl" : "text-xl md:text-2xl"}`}>{doc.strain_name}</div>
          </div>
        </div>
        {hasChildren && (
          <>
            <div className="flex justify-center my-3">
              <div className="w-px h-8" style={{ background: "var(--gold)" }}></div>
            </div>
            <div className="flex flex-wrap items-stretch justify-center gap-3 md:gap-6">
              {children.map((c, i) => (
                <TreeNode
                  key={c.id}
                  label={c.strain_name}
                  slug={c.slug || c.id}
                  status={c.verification_status}
                  hasListings={c.has_listings}
                  role="child"
                  compact={compact}
                  testid={`parentage-node-child-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {!compact && doc.additional_parents && (
        <div className="mt-6 pt-4 border-t border-[var(--border)] text-sm" style={{ color: "var(--text-2)" }}>
          <strong>Complex lineage:</strong> {doc.additional_parents}
        </div>
      )}
      {!compact && doc.lineage_notes && (
        <div className="mt-4 pt-4 border-t border-[var(--border)] text-sm leading-relaxed whitespace-pre-line"
             style={{ color: "var(--text-2)" }}>
          {doc.lineage_notes}
        </div>
      )}
    </section>
  );
}

function TreeNode({ label, slug, status, hasListings, role, testid, compact = false }) {
  const verified = status === "verified";
  const Wrap = slug ? Link : "div";
  const wrapProps = slug ? { to: `/strains/${slug}` } : {};
  const accent = verified ? "var(--primary-text)" : "var(--text-muted)";
  const padding = compact ? "px-2.5 py-2 md:px-3 md:py-2.5" : "px-4 py-3 md:px-6 md:py-4";
  const labelSize = compact ? "text-xs md:text-sm" : "text-base md:text-lg";
  const minW = compact ? "min-w-[100px] md:min-w-[140px]" : "min-w-[140px] md:min-w-[170px]";
  return (
    <Wrap {...wrapProps}
          className={`bg-[var(--surface-2)] border ${padding} text-center ${minW} relative ${slug ? "hover:border-[var(--gold)] transition-colors cursor-pointer" : ""}`}
          style={{ borderColor: slug ? accent : "var(--border)", opacity: compact ? 0.92 : 1 }}
          data-testid={testid}>
      <div className="label-eyebrow mb-1" style={{ color: "var(--text-muted)" }}>
        {role === "grandparent" ? "G-parent" : role === "parent" ? "Parent" : "Descendant"}
      </div>
      <div className={`font-serif ${labelSize} leading-tight`} style={{ color: "var(--text)" }}>{label}</div>
      <div className="flex items-center justify-center gap-2 mt-2 text-[9px] font-mono uppercase tracking-[0.14em]">
        {slug ? (
          <span style={{ color: accent }}>{status || "linked"}</span>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>unmapped</span>
        )}
        {hasListings && (
          <span className="inline-flex items-center gap-0.5" style={{ color: "var(--primary-text)" }}>
            <ShoppingBag className="w-2.5 h-2.5"/> live
          </span>
        )}
      </div>
    </Wrap>
  );
}
